'use strict';

import './components/index';

import { formatCardDate } from '@core/utils/date';
import { goToDetailsView } from '@core/utils/navigate';
import {
    updateFiltersStateHistoryPage,
    updateLastSelectedElement,
} from '@core/utils/history-state';
import { isVideoItem, handleMetaInfoParam } from '@core/utils/active';
import { addDownloadOptionsList } from '@core/utils/download';
import { updateSessionStoreValue } from '@core/utils/sessionStore';
import { loadVideoWithPartialContentSupport } from '@core/utils/api';
import {
    TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
    UPDATE_LANGUAGE_EVENT,
    TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
    TRIGGER_CHANGE_DESCRIPTION_ACTIVES_EVENT,
} from '@core/constants/EVENTS';
import {
    WESOG_SESSION_STORE_PARAMS,
    WESOG_STORE_CONFIGURATION_PARAMS,
    WESOG_STORE_PARAMS,
} from '@core/constants/STORE';
import { DEFAULT_LANGUAGE, MIN_ACTIVES_HEIGHT } from '@core/constants/DEFAULT';
import { COMMON_EXCLUSIVE_WATERMARK_IMG } from '@core/constants/WATERMARK';
import { ZOOM_DOWN, MAX_FLEX_GROW_VALUE } from '@core/constants/CONSTANTS';
import { MAX_LIGHTBOX_FLEX_GROW_VALUE } from '@core/constants/LIGHTBOX';
import { META_INFO_KEYS } from '@core/constants/ACTIVES';
import { PROV_FILTERS } from '@core/constants/API';
import { getStoreValue } from '@core/utils/store';
import { ROUTES } from '@core/constants/ROUTES';
import { ripple } from '@core/utils/ripple';
import { SERVICE_WORKER_VERSION } from '@core/constants/SERVICE-WORKER';
import { customDebounce, cleanTextToCards, isDetailsView } from '@core/utils/utils';
import {
    globalSharedStyles,
    typographySharedStyles,
    commonCardStyles,
    imageIconsStyles,
    customCommonStyles,
    customDownloadOptionsStyles,
    commonCustomModalStyles,
    customMenuDownStyleSheet,
} from '@core/styles';

import { calculateZoomValue } from '../../utils';
import {
    SEARCH_IMG_CARD_ATTRIBUTES,
    SEARCH_IMG_CARD_WEB_COMPONENT,
    CUSTOM_SEARCH_IMG_CARD_LOADING_SPINNER_WEB_COMPONENT,
} from './CONSTANTS';
import {
    handleDescriptionActive,
    handleTranslationHelper,
    handleVideoItemConfiguration,
    configureIconAttributes,
    configureIconContainers,
} from './utils';

import htmlContent from './SearchImgCardComponent.html';
import cssContent from './SearchImgCardComponent.css';

/**
 * SearchImgCardComponent
 *
 * A web component that displays search result image cards with interactive features
 * including zoom, cart, lightbox, download, and video playback capabilities.
 *
 * @class SearchImgCardComponent
 * @extends {HTMLElement}
 */
class SearchImgCardComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            typographySharedStyles,
            commonCardStyles,
            imageIconsStyles,
            customCommonStyles,
            customDownloadOptionsStyles,
            commonCustomModalStyles,
            customMenuDownStyleSheet,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        /** @type {Object} */
        this._img = null;
        /** @type {number} */
        this._minImgHeight = null;
        /** @type {boolean} */
        this._isVideoLoaded = false;
        /** @type {boolean} */
        this._isAddingElementToLightBox = false;
        /** @type {boolean} */
        this._shouldPlayVideo = false;
        /** @type {HTMLVideoElement|null} */
        this._videoElement = null;
        /** @type {HTMLImageElement|null} */
        this._imgElement = null;
        /** @type {HTMLDivElement|null} */
        this._container = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleUpdateZoomActivesBound = this._handleUpdateZoomActives.bind(this);
        this._handleShowDownloadOptionsBound = this._handleShowDownloadOptions.bind(this);
        this._handleChangeDescriptionActivesBound =
            this._handleChangeDescriptionActives.bind(this);
        this._handleContainerClickBound = this._handleContainerClick.bind(this);
        this._handleMouseEnterBound = this._handleMouseEnter.bind(this);
        this._handleMouseLeaveBound = this._handleMouseLeave.bind(this);

        // Debounced handlers
        this._debouncedMouseEnter = customDebounce(this._handleMouseEnterBound, 100);
        this._debouncedMouseLeave = customDebounce(this._handleMouseLeaveBound, 100);
    }

    /**
     * Called when the element is connected to the DOM.
     * @returns {void}
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._addRippleBehavior();
        this._setupInitialData();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM.
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
        this._clearTimeouts();
    }

    /**
     * Cache DOM references for better performance.
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._container = this.shadowRoot.querySelector('[data-main-card-container]');
        this._imgElement = this.shadowRoot.querySelector('[data-main-img]');
        this._videoElement = this.shadowRoot.querySelector('video');
    }

    /**
     * Add event listeners to the component.
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.addEventListener(
            TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
            this._handleUpdateZoomActivesBound,
        );
        document.addEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
            this._handleShowDownloadOptionsBound,
        );
        document.addEventListener(
            TRIGGER_CHANGE_DESCRIPTION_ACTIVES_EVENT,
            this._handleChangeDescriptionActivesBound,
        );

        if (this._container) {
            this._container.addEventListener('click', this._handleContainerClickBound, {
                passive: true,
            });
        }
    }

    /**
     * Remove event listeners from the component.
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.removeEventListener(
            TRIGGER_UPDATE_ZOOM_ACTIVES_EVENT,
            this._handleUpdateZoomActivesBound,
        );
        document.removeEventListener(
            TRIGGER_SHOW_DOWNLOAD_OPTIONS_CARD_EVENT,
            this._handleShowDownloadOptionsBound,
        );
        document.removeEventListener(
            TRIGGER_CHANGE_DESCRIPTION_ACTIVES_EVENT,
            this._handleChangeDescriptionActivesBound,
        );

        if (this._container) {
            this._container.removeEventListener('click', this._handleContainerClickBound);
            this._container.removeEventListener('mouseenter', this._debouncedMouseEnter);
            this._container.removeEventListener('mouseleave', this._debouncedMouseLeave);
        }
    }

    /**
     * Clear any active timeouts to prevent memory leaks.
     * @returns {void}
     * @private
     */
    _clearTimeouts() {
        if (this._mouseEnterTimeout) {
            clearTimeout(this._mouseEnterTimeout);
            this._mouseEnterTimeout = null;
        }
        if (this._mouseLeaveTimeout) {
            clearTimeout(this._mouseLeaveTimeout);
            this._mouseLeaveTimeout = null;
        }
    }

    /**
     * Render the component.
     * @param {boolean} [firstTime=true] - Whether this is the first render.
     * @returns {void}
     */
    render(firstTime = true) {
        if (!this._img) {
            console.warn('SearchImgCardComponent: No image data provided');
            return;
        }

        this._hideLoadingSpinner();
        this._setupContainerStyles();
        this._setupImageElement();
        this._setupDescription();
        this._setupVideoConfiguration();
        this._setupTitle();
        this._setupIconConfiguration();
        this._setupMetaInfo();

        if (firstTime) {
            this._setupVideoEventListeners();
        }
    }

    /**
     * Hide the loading spinner.
     * @returns {void}
     * @private
     */
    _hideLoadingSpinner() {
        const spinner = this.shadowRoot.querySelector(
            CUSTOM_SEARCH_IMG_CARD_LOADING_SPINNER_WEB_COMPONENT,
        );
        if (spinner) {
            spinner.style.display = 'none';
        }
    }

    /**
     * Setup container styles based on image dimensions.
     * @returns {void}
     * @private
     */
    _setupContainerStyles() {
        if (!this._container || !this._img) return;

        const customFlexGrow =
            this._img.img_width !== 'auto'
                ? (this._img.img_width * this._minImgHeight) / this._img.img_height
                : 0;
        const customWidth =
            this._img.img_width !== 'auto'
                ? `${(this._img.img_width * this._minImgHeight) / this._img.img_height}px`
                : 'auto';

        this._container.style.width = customWidth;
        this._container.style.minHeight = `${this._minImgHeight}px`;
        this._container.style.flexGrow = customFlexGrow;
    }

    /**
     * Setup the main image element.
     * @returns {void}
     * @private
     */
    _setupImageElement() {
        if (!this._imgElement || !this._img) return;

        const descriptionLabel = cleanTextToCards(
            this._img?.caption || this._img?.description || '',
        );

        this._imgElement.src = this._img.img_src;
        this._imgElement.alt = descriptionLabel;

        const descriptionElement = this.shadowRoot.querySelector(
            '.img-title-description > p',
        );
        if (descriptionElement) {
            descriptionElement.innerText = descriptionLabel;
        }
    }

    /**
     * Setup the description section.
     * @returns {void}
     * @private
     */
    _setupDescription() {
        if (!this._img) return;

        if (isVideoItem(this._img.img_zoom)) {
            handleVideoItemConfiguration(this.shadowRoot, this._img);
        } else {
            handleDescriptionActive(this.shadowRoot);
        }
    }

    /**
     * Setup video configuration.
     * @returns {void}
     * @private
     */
    _setupVideoConfiguration() {
        if (!this._img || !isVideoItem(this._img.img_zoom)) return;

        const imgSimilarImagesContainer = this.shadowRoot.querySelector(
            '[data-similar-images-icon]',
        );
        if (imgSimilarImagesContainer) {
            imgSimilarImagesContainer.style.display = 'none';
        }
    }

    /**
     * Setup the title element.
     * @returns {void}
     * @private
     */
    _setupTitle() {
        if (!this._img) return;

        const titleElement = this.shadowRoot.querySelector('.img-title-description > h3');
        if (titleElement) {
            titleElement.innerText = this._img?.date
                ? formatCardDate(this._img.date)
                : this._img.reference || '';
        }
    }

    /**
     * Setup icon configuration.
     * @returns {void}
     * @private
     */
    _setupIconConfiguration() {
        if (!this._img) return;

        const customFlexGrow =
            this._img.img_width !== 'auto'
                ? (this._img.img_width * this._minImgHeight) / this._img.img_height
                : 0;
        const tooltipContentWidth =
            this._img.img_width === 'auto' ? 150 : Math.min(customFlexGrow * 0.8, 150);
        const imgUid = this._img.uid || this._img.id;
        const isLightboxItem =
            this.getAttribute(SEARCH_IMG_CARD_ATTRIBUTES.LIGHTBOX_ITEM) === 'true';

        configureIconAttributes(
            this.shadowRoot,
            this._img,
            customFlexGrow,
            tooltipContentWidth,
            imgUid,
            isLightboxItem,
        );

        configureIconContainers(
            this.shadowRoot,
            customFlexGrow,
            MAX_FLEX_GROW_VALUE,
            MAX_LIGHTBOX_FLEX_GROW_VALUE,
            isLightboxItem,
        );

        this._setupZoomConfiguration();
        this._setupProviderConfiguration();
    }

    /**
     * Setup zoom configuration.
     * @returns {void}
     * @private
     */
    _setupZoomConfiguration() {
        const imgSimilarImagesContainer = this.shadowRoot.querySelector(
            '[data-similar-images-icon]',
        );
        const imgFavIconTooltipContainers =
            this.shadowRoot.querySelectorAll('[data-img-icons]');

        if (isDetailsView()) {
            const zoomModalIcon = this.shadowRoot.querySelector('[data-zoom-modal-icon]');
            if (zoomModalIcon) {
                zoomModalIcon.style.display = 'none';
            }
        }

        const zoomConfig = getStoreValue(WESOG_STORE_PARAMS.CONFIGURATION)[
            WESOG_STORE_CONFIGURATION_PARAMS.ZOOM
        ];

        const handleZoomIn = zoomConfig === 50 || isDetailsView();

        if (imgSimilarImagesContainer) {
            imgSimilarImagesContainer.setAttribute(
                SEARCH_IMG_CARD_ATTRIBUTES.ZOOM_IN,
                handleZoomIn,
            );
        }

        if (handleZoomIn) {
            const imgIconsContainerSize = 17 * ZOOM_DOWN;

            Array.from(imgFavIconTooltipContainers).forEach((container) => {
                container.querySelectorAll('& > *').forEach((element) => {
                    element.style.width = `${imgIconsContainerSize}px`;
                    element.style.height = `${imgIconsContainerSize}px`;
                    element.style.maxWidth = `${imgIconsContainerSize}px`;
                    element.style.padding = '6px';

                    setTimeout(() => {
                        element.setAttribute(SEARCH_IMG_CARD_ATTRIBUTES.ZOOM_IN, 'true');
                    }, 0);
                });
            });
        } else {
            const imgIconsContainerSize = 30;

            Array.from(imgFavIconTooltipContainers).forEach((container) => {
                container.querySelectorAll('& > *').forEach((element) => {
                    element.style.width = `${imgIconsContainerSize}px`;
                    element.style.height = `${imgIconsContainerSize}px`;
                    element.style.maxWidth = `${imgIconsContainerSize}px`;
                    element.style.padding = '4px';

                    setTimeout(() => {
                        element.setAttribute(SEARCH_IMG_CARD_ATTRIBUTES.ZOOM_IN, 'false');
                    }, 0);
                });
            });
        }
    }

    /**
     * Setup provider configuration.
     * @returns {void}
     * @private
     */
    _setupProviderConfiguration() {
        if (!this._img || this._img.wesog_provider === PROV_FILTERS.GTRES) return;

        const elements = [
            this.shadowRoot.querySelector('[data-add-cart-icon]'),
            this.shadowRoot.querySelector('[data-delete-icon]'),
            this.shadowRoot.querySelector('[data-star-icon]'),
            this.shadowRoot.querySelector('[data-add-lightbox-icon]'),
        ];

        elements.forEach((element) => {
            if (element) {
                element.style.display = 'none';
            }
        });
    }

    /**
     * Setup meta information.
     * @returns {void}
     * @private
     */
    _setupMetaInfo() {
        if (!this._img?.MetaInfo || !this._container) return;

        const keys = handleMetaInfoParam(this._img.MetaInfo);
        const extraImgIcons = this.shadowRoot.querySelector('[data-extra-img-icons]');
        const premiumIcon = this.shadowRoot.querySelector('[data-premium-icon]');
        const generativeAIIcon = this.shadowRoot.querySelector(
            '[data-generative-ai-icon]',
        );

        keys.forEach((key) => {
            if (key === META_INFO_KEYS.AI || key === META_INFO_KEYS.PREMIUM) {
                if (extraImgIcons) {
                    extraImgIcons.style.display = 'flex';
                }

                if (key === META_INFO_KEYS.PREMIUM && premiumIcon) {
                    premiumIcon.style.display = 'flex';
                }

                if (key === META_INFO_KEYS.AI && generativeAIIcon) {
                    generativeAIIcon.style.display = 'flex';
                }
            }

            if (key === META_INFO_KEYS.EXCLUSIVE) {
                this._addExclusiveWatermark();
            }
        });
    }

    /**
     * Add exclusive watermark to the image.
     * @returns {void}
     * @private
     */
    _addExclusiveWatermark() {
        if (!this._container || !this._imgElement) return;

        const exclusiveImg = document.createElement('img');
        exclusiveImg.src = `assets/img/exclusive_watermark_${
            getStoreValue(WESOG_STORE_PARAMS.CURRENT_LANGUAGE) || DEFAULT_LANGUAGE
        }.svg`;
        exclusiveImg.alt = 'Exclusive watermark';
        exclusiveImg.classList.add(COMMON_EXCLUSIVE_WATERMARK_IMG);

        this._container.insertBefore(exclusiveImg, this._imgElement);
    }

    /**
     * Setup initial data from attributes.
     * @returns {void}
     * @private
     */
    _setupInitialData() {
        this._img = JSON.parse(this.getAttribute(SEARCH_IMG_CARD_ATTRIBUTES.IMAGE));
        this._minImgHeight = parseInt(
            this.getAttribute(SEARCH_IMG_CARD_ATTRIBUTES.MIN_IMG_HEIGHT),
        );
    }

    /**
     * Setup video event listeners.
     * @returns {void}
     * @private
     */
    _setupVideoEventListeners() {
        if (!this._img || !isVideoItem(this._img.img_zoom) || !this._container) return;

        this._container.addEventListener('mouseenter', this._debouncedMouseEnter, {
            passive: true,
        });
        this._container.addEventListener('mouseleave', this._debouncedMouseLeave, {
            passive: true,
        });
    }

    /**
     * Handle mouse enter event for video playback.
     * @param {Event} event - The mouse enter event.
     * @returns {void}
     * @private
     */
    async _handleMouseEnter(event) {
        if (
            !this._img ||
            !isVideoItem(this._img.img_zoom) ||
            !this._videoElement ||
            !this._imgElement
        )
            return;

        event.preventDefault();
        event.stopPropagation();

        this._showLoadingSpinner();
        this._shouldPlayVideo = true;

        try {
            const videoUrl = await this._getVideoUrl();
            const videoSourceElement = this._videoElement.querySelector('source');

            if (videoSourceElement) {
                videoSourceElement.setAttribute('src', videoUrl);
                this._videoElement.load();
                await this._videoElement.play();

                if (!this._shouldPlayVideo) {
                    this._videoElement.pause();
                }

                this._isVideoLoaded = true;
                this._imgElement.style.display = 'none';
                this._videoElement.style.display = 'block';
            }
        } catch (error) {
            console.error('Error loading video:', error);
        } finally {
            this._hideLoadingSpinner();
        }
    }

    /**
     * Handle mouse leave event for video playback.
     * @param {Event} event - The mouse leave event.
     * @returns {void}
     * @private
     */
    _handleMouseLeave(event) {
        if (
            !this._img ||
            !isVideoItem(this._img.img_zoom) ||
            !this._videoElement ||
            !this._imgElement
        )
            return;

        event.preventDefault();
        event.stopPropagation();
        this._shouldPlayVideo = false;

        if (!this._container?.contains(event.relatedTarget)) {
            this._hideLoadingSpinner();
            this._imgElement.style.display = 'block';
            this._videoElement.style.display = 'none';

            if (this._isVideoLoaded) {
                this._videoElement.pause();
            }
        }
    }

    /**
     * Get video URL with caching support.
     * @returns {Promise<string>} The video URL.
     * @private
     */
    async _getVideoUrl() {
        const cache = await caches.open(SERVICE_WORKER_VERSION);
        const cachedResponse = await cache.match(this._img.img_zoom);

        if (cachedResponse) {
            return URL.createObjectURL(await cachedResponse.blob());
        }

        const response = await loadVideoWithPartialContentSupport(
            fetch,
            this._img.img_zoom,
        );
        const responseClone = response.clone();
        const videoUrl = URL.createObjectURL(await response.blob());

        await cache.put(this._img.img_zoom, responseClone);
        return videoUrl;
    }

    /**
     * Show the loading spinner.
     * @returns {void}
     * @private
     */
    _showLoadingSpinner() {
        const spinner = this.shadowRoot.querySelector(
            CUSTOM_SEARCH_IMG_CARD_LOADING_SPINNER_WEB_COMPONENT,
        );
        if (spinner) {
            spinner.style.display = 'flex';
        }
    }

    /**
     * Handle container click event.
     * @returns {void}
     * @private
     */
    _handleContainerClick() {
        if (!this._img) return;

        const imgUid = this._img.uid || this._img.id;

        updateSessionStoreValue(WESOG_SESSION_STORE_PARAMS.CURRENT_SELECTED_IMAGE, {
            uid: imgUid,
            ...this._img,
        });

        if (!window.location.pathname.includes(ROUTES.DETAILS)) {
            updateFiltersStateHistoryPage(this._img.current_page || 0);
            updateLastSelectedElement(imgUid);
        }

        setTimeout(() => {
            goToDetailsView(imgUid);
        }, 500);
    }

    /**
     * Handle translation helper.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        handleTranslationHelper(
            this.shadowRoot,
            getStoreValue(WESOG_STORE_PARAMS.CURRENT_LANGUAGE),
            DEFAULT_LANGUAGE,
        );
    }

    /**
     * Handle update zoom actives event.
     * @returns {void}
     * @private
     */
    _handleUpdateZoomActives() {
        if (window.location.pathname.includes(ROUTES.DETAILS)) return;

        this._minImgHeight = MIN_ACTIVES_HEIGHT * calculateZoomValue();
        if (this._img) {
            this._img.img_height = this._img.img_height || this._minImgHeight;
        }
        this.render(false);
    }

    /**
     * Handle show download options event.
     * @param {CustomEvent} event - The event.
     * @returns {void}
     * @private
     */
    _handleShowDownloadOptions(event) {
        if (!this._img || event.detail.elementId !== this._img.uid) return;

        addDownloadOptionsList(this, event.detail.options, this._img.reference);
        this._handleTranslationHelper();
    }

    /**
     * Handle change description actives event.
     * @returns {void}
     * @private
     */
    _handleChangeDescriptionActives() {
        if (!this._img || isVideoItem(this._img.img_zoom)) return;

        handleDescriptionActive(this.shadowRoot);
    }

    /**
     * Add ripple behavior to elements with data-ripple attribute.
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        this.shadowRoot.querySelectorAll('[data-ripple]').forEach((element) => {
            ripple(element);
        });
    }
}

customElements.define(SEARCH_IMG_CARD_WEB_COMPONENT, SearchImgCardComponent);
